<template>
  <Menu
    as="div"
    v-slot="{ close }"
    class="relative notifications-menu mx-[8px] md:mx-[10px] lg:mx-[12px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
  >
    <MenuButton
      class="leading-none inline-block transition-all relative top-[2px] hover:text-primary-500"
    >
      <i class="material-symbols-outlined !text-[22px] md:!text-[24px]">
        notifications
      </i>
      <span
        v-if="unreadCount > 0"
        class="top-[3px] ltr:right-[4px] rtl:left-[4px] w-[6px] h-[6px] rounded-full absolute bg-orange-500"
      ></span>
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="bg-white dark:bg-[#0c1427] transition-all shadow-3xl dark:shadow-none py-[17px] absolute mt-[17px] md:mt-[20px] w-[290px] md:w-[350px] z-[1] top-full ltr:-right-[120px] ltr:md:right-0 rtl:-left-[120px] rtl:md:left-0 rounded-md focus:outline-hidden"
      >
        <div class="flex items-center justify-between px-[20px] pb-[17px]">
          <span class="font-semibold text-black dark:text-white text-[15px]">
            {{ directT('navbar.notifications') }}
            <span
              class="text-gray-500 dark:text-gray-400 font-normal text-base"
            >
              ({{ unreadCount }})
            </span>
          </span>
          <a href="javascript:void(0);" @click="markAllAsRead(); close()" class="text-primary-500">{{ directT('navbar.clearAll') }}</a>
        </div>

        <div v-if="loading" class="flex justify-center py-4">
          <div class="inline-block animate-spin rounded-full border-4 border-primary-500 border-t-transparent h-8 w-8"></div>
        </div>
        
        <div v-else-if="error" class="text-center py-4 text-danger-500">
          {{ error }}
        </div>
        
        <div v-else-if="notifications.length === 0" class="text-center py-4 text-gray-500">
          {{ directT('navbar.noNotifications') }}
        </div>

        <ul v-else class="mb-[18px]">
          <li
            v-for="notification in notifications"
            :key="notification.id"
            class="relative border-b border-gray-100 dark:border-[#172036] border-dashed py-[17px] ltr:pl-[75px] ltr:pr-[20px] rtl:pr-[75px] rtl:pl-[20px] first:border-t first:border-gray-100 dark:first:border-[#172036]"
          >
            <div
              class="rounded-full flex items-center justify-center absolute text-center transition-all top-1/2 -translate-y-1/2 ltr:left-[20px] rtl:right-[20px] w-[44px] h-[44px] text-primary-500 bg-[#4936f50d]"
            >
              <i class="material-symbols-outlined !text-[22px]">
                {{ getNotificationIcon(notification.type) }}
              </i>
            </div>
            <span class="block mb-[3px] text-black dark:text-white">
              {{ notification.content }}
            </span>
            <span class="block text-sm text-gray-500">
              {{ formatDate(notification.timestamp) }}
            </span>
            <button
              @click="markAsRead(notification); close()"
              class="block left-0 top-0 right-0 bottom-0 z-[1] absolute"
            ></button>
            <span
              v-if="notification.status === 'unread'"
              class="inline-block rounded-full bg-primary-500 absolute w-[6px] h-[6px] right-[20px] top-1/2 -translate-y-1/2"
            ></span>
          </li>
        </ul>
        <div class="text-center">
          <RouterLink
            to="/notifications"
            class="inline-block font-medium relative text-primary-500 transition-all hover:underline"
            @click="close()"
          >
            {{ directT('navbar.seeAllNotifications') }}
          </RouterLink>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { Menu, MenuButton, MenuItems } from "@headlessui/vue";
import { supabase } from "@/utils/supabase";
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Notification {
  id: string;
  userid: string;
  timestamp: string;
  type: string;
  content: string;
  status: string;
}

export default defineComponent({
  name: "NotificationsLists",
  components: {
    Menu,
    MenuButton,
    MenuItems,
  },
  setup() {
    const { t, locale } = useI18n();
    const notifications = ref<Notification[]>([]);
    const loading = ref(false);
    const error = ref('');
    let refreshInterval: number | null = null;
    let notificationsSubscription: any = null;
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    const unreadCount = computed(() => {
      return notifications.value.filter(n => n.status === 'unread').length;
    });
    
    const fetchNotifications = async () => {
      try {
        loading.value = true;
        error.value = '';
        
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          throw new Error(userError.message);
        }
        
        if (!userData || !userData.user) {
          throw new Error("No authenticated user found");
        }
        
        const { data, error: notifError } = await supabase
          .from('notifications')
          .select('*')
          .eq('userid', userData.user.id)
          .eq('status', 'unread')
          .order('timestamp', { ascending: false })
          .limit(5);
          
        if (notifError) {
          throw new Error(notifError.message);
        }
        
        notifications.value = data || [];
      } catch (err) {
        console.error("Error fetching notifications:", err);
        error.value = err instanceof Error ? err.message : "Failed to load notifications";
      } finally {
        loading.value = false;
      }
    };
    
    const markAsRead = async (notification: Notification) => {
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ status: 'read' })
          .eq('id', notification.id);
          
        if (error) {
          throw new Error(error.message);
        }
        
        // Update local state
        const index = notifications.value.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          notifications.value.splice(index, 1);
        }
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    };
    
    const markAllAsRead = async () => {
      try {
        if (notifications.value.length === 0) {
          console.log("No notifications to mark as read");
          return;
        }
        
        console.log("Marking all notifications as read");
        
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) {
          console.error("No authenticated user found");
          return;
        }
        
        loading.value = true;
        
        const { data, error } = await supabase
          .from('notifications')
          .update({ status: 'read' })
          .eq('userid', userData.user.id)
          .eq('status', 'unread')
          .select();
          
        if (error) {
          console.error("Failed to update notifications:", error);
          throw new Error(error.message);
        }
        
        console.log(`Successfully marked ${data?.length || 0} notifications as read`);
        
        // רענן את רשימת ההתראות
        await fetchNotifications();
      } catch (err) {
        console.error("Error marking all notifications as read:", err);
        // במקרה של שגיאה, רענן את ההתראות בכל זאת כדי לוודא שהמצב סנכרוני
        await fetchNotifications();
      } finally {
        loading.value = false;
      }
    };
    
    // הוספת Supabase Realtime הקשבה לשינויים
    const setupRealtimeListener = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) return;
        
        // הקשבה לטבלת notifications
        notificationsSubscription = supabase
          .channel('notifications-changes')
          .on('postgres_changes', 
            { 
              event: '*', 
              schema: 'public', 
              table: 'notifications',
              filter: `userid=eq.${userData.user.id}`
            }, 
            (payload) => {
              console.log('Realtime notification change:', payload);
              
              // רענון ההתראות כשיש שינוי
              fetchNotifications();
            }
          )
          .subscribe();
          
        console.log('Realtime subscription setup for notifications');
      } catch (err) {
        console.error('Error setting up realtime listener:', err);
      }
    };
    
    // הוספת רענון עיתי
    const setupRefreshInterval = () => {
      refreshInterval = window.setInterval(() => {
        fetchNotifications();
      }, 30000); // רענון כל 30 שניות
    };
    
    const formatDate = (dateString: string) => {
      const now = new Date();
      const date = new Date(dateString);
      const diff = now.getTime() - date.getTime();
      
      // Less than a minute
      if (diff < 60 * 1000) {
        return 'just now';
      }
      
      // Less than an hour
      if (diff < 60 * 60 * 1000) {
        const minutes = Math.floor(diff / (60 * 1000));
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }
      
      // Less than a day
      if (diff < 24 * 60 * 60 * 1000) {
        const hours = Math.floor(diff / (60 * 60 * 1000));
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
      
      // Less than a week
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.floor(diff / (24 * 60 * 60 * 1000));
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }
      
      // Format as date
      return date.toLocaleDateString();
    };
    
    const getNotificationIcon = (type: string) => {
      switch (type) {
        case 'alert':
          return 'notifications_active';
        case 'information':
          return 'info';
        case 'announcement':
          return 'campaign';
        default:
          return 'notifications';
      }
    };
    
    onMounted(() => {
      fetchNotifications();
      setupRealtimeListener();
      setupRefreshInterval();
    });
    
    onUnmounted(() => {
      // ניקוי הקשבה בזמן אמת ורענון עיתי
      if (notificationsSubscription) {
        notificationsSubscription.unsubscribe();
      }
      
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    });
    
    return {
      notifications,
      loading,
      error,
      unreadCount,
      markAsRead,
      markAllAsRead,
      formatDate,
      getNotificationIcon,
      directT
    };
  }
});
</script>
